/**
 * VAST-related TypeScript types and interfaces
 */

// Configuration for VAST service
export interface VASTServiceConfig {
  timeout?: number                   // Request timeout in ms
  withCredentials?: boolean          // Send cookies with VAST requests
  wrapperLimit?: number             // Max number of wrapper redirects
  maxBitrate?: number               // Max bitrate for media file selection
}

// Ad configuration from video config
export interface VASTAdConfig {
  id: string
  type: 'pre-roll' | 'mid-roll' | 'post-roll'
  vastUrl?: string                 // URL to VAST XML
  vastXml?: string                 // Inline VAST XML
  scheduled?: number               // Timestamp for mid-rolls (in seconds)
  /**
   * The publisher's ceiling on the wait before the skip button, in seconds.
   *
   * Combined with the creative's own `skipoffset` by taking the smaller of the two, so a tag
   * that declares none becomes skippable and one that declares a longer wait is cut short.
   * Undefined leaves the decision to the creative.
   */
  forceSkipAfter?: number
}

// Internal representation of a parsed ad
export interface ParsedAd {
  id: string
  type: 'pre-roll' | 'mid-roll' | 'post-roll'
  mediaFiles: MediaFile[]
  duration: number
  skipOffset?: number              // When skip button appears (-1 for no skip)
  clickThrough?: string            // Landing page URL
  clickTracking: string[]          // Click tracking URLs
  trackingEvents: TrackingEvent[]
  impressionUrls: string[]
  errorUrls: string[]
  companionAds?: CompanionAd[]
  adSystem?: string
  adTitle?: string
  description?: string
  advertiser?: string
  pricing?: string
  survey?: string
  expires?: string
}

export interface MediaFile {
  url: string
  mimeType: string
  width: number
  height: number
  bitrate?: number
  scalable?: boolean
  maintainAspectRatio?: boolean
  codec?: string
  delivery?: 'progressive' | 'streaming'
}

export interface TrackingEvent {
  event: TrackingEventType
  urls: string[]
}

export type TrackingEventType = 
  | 'creativeView'
  | 'start'
  | 'firstQuartile'
  | 'midpoint'
  | 'thirdQuartile'
  | 'complete'
  | 'mute'
  | 'unmute'
  | 'pause'
  | 'resume'
  | 'rewind'
  | 'skip'
  | 'playerExpand'
  | 'playerCollapse'
  | 'fullscreen'
  | 'exitFullscreen'
  | 'acceptInvitationLinear'
  | 'closeLinear'
  | 'click'

export interface CompanionAd {
  id?: string
  width: number
  height: number
  assetWidth?: number
  assetHeight?: number
  expandedWidth?: number
  expandedHeight?: number
  apiFramework?: string
  adSlotId?: string
  creativeType: 'image' | 'html' | 'iframe'
  resource: CompanionResource
  clickThrough?: string
  clickTracking: string[]
  altText?: string
  companionClickTracking: string[]
  trackingEvents: TrackingEvent[]
}

export interface CompanionResource {
  staticResource?: {
    url: string
    creativeType: string
  }
  htmlResource?: string
  iframeResource?: string
}

// Error types
export class VASTError extends Error {
  code: number
  
  constructor(message: string, code: number, originalError?: any) {
    super(message)
    this.name = 'VASTError'
    this.code = code
    
    if (originalError) {
      this.stack = originalError.stack
    }
  }
}

// VAST Error codes (IAB standard)
export enum VASTErrorCode {
  // XML Parsing errors
  XML_PARSE_ERROR = 100,
  VAST_SCHEMA_ERROR = 101,
  VAST_VERSION_ERROR = 102,
  
  // Trafficking errors
  TRAFFICKING_ERROR = 200,
  VIDEO_TIMEOUT = 201,
  WRAPPER_LIMIT_REACHED = 302,
  NO_ADS_VAST_RESPONSE = 303,
  
  // Linear Ad errors
  LINEAR_ERROR = 400,
  FILE_NOT_FOUND = 401,
  MEDIA_TIMEOUT = 402,
  MEDIA_FILE_NO_SUPPORTED_TYPE = 403,
  MEDIA_FILE_DISPLAY_ERROR = 405,
  
  // General errors
  UNDEFINED_ERROR = 900,
  VPAID_ERROR = 901
}

// Ad state for tracking
export interface AdState {
  ad: ParsedAd | null
  isPlaying: boolean
  progress: number        // 0-100
  currentTime: number     // seconds
  duration: number        // seconds
  volume: number          // 0-100
  isMuted: boolean
  isFullscreen: boolean
  canSkip: boolean
  skipIn: number         // seconds remaining until skip
  error: VASTError | null
  // Tracking state
  firedEvents: Set<TrackingEventType>
  quartileEvents: {
    firstQuartile: boolean
    midpoint: boolean
    thirdQuartile: boolean
  }
}


// Macros for URL replacement
export interface MacroData {
  CACHEBUSTER: string
  TIMESTAMP: string
  ASSETURI: string
  CONTENTPLAYHEAD: string
  MEDIAPLAYHEAD: string
  ERRORCODE?: string
  // Player-specific
  PLAYERSIZE: string
  PLAYERSTATE: string
  INVENTORYSTATE: string
  CLICKPOS: string
}

// Ad scheduling
export interface AdBreak {
  id: string
  time: number               // When to show the ad (seconds)
  ads: VASTAdConfig[]
  played: boolean
}